import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle, XCircle } from "@phosphor-icons/react";
import { PageHeader } from "../components/PageHeader";
import { FadeUp, Overline } from "../components/Reveal";

const LEAVING = [
  "Elephant-sized incumbents that rent your own capability back to you",
  "Point-solution vendors that fix one slice and never talk to each other",
  "Heavyweight service providers — slow, costly, months to deliver",
  "Centralised platforms that quietly own your data, value and roadmap",
];

const MOVING_TO = [
  "One lean, autonomous platform that self-evolves with your needs",
  "Decentralised and distributed — resilient, with no single gatekeeper",
  "Data-sovereign by default — your data and its value stay with you",
  "Compliant-by-default agents delivering work at the speed of a prompt",
];

const SIX_DS = [
  ["Default Compliant", "Compliance is the starting point, never an afterthought. Every output, workflow and dataset is built to satisfy HIPAA, GxP, 21 CFR Part 11, GDPR and the EU AI Act from the first click."],
  ["Decentralised", "No single point of control. Data, decisions and value are spread across patients, providers, payers and partners — never concentrated inside one platform."],
  ["Democratic", "Collective decision-making, not top-down control. Access, governance and benefits are open to patients, clinicians, organisations and regulators alike."],
  ["Distributed", "Power and infrastructure are spread across many nodes and many people, making the system resilient, scalable and free of a single point of failure."],
  ["Data-sovereign", "People and communities own and control their own data. You decide who can access it, for what purpose and for how long."],
  ["Decommodified", "People and their data are never reduced to tradeable assets. Value created by a community flows back to that community rather than being mined for profit."],
];

const THREE_PS = [
  ["Personalised", "Care, insights and recommendations are tailored to the individual — your history, biology and context — instead of a one-size-fits-all template."],
  ["Precision", "Decisions are driven by precise, evidence-based data and analytics: the right intervention, for the right person, at the right time."],
  ["Patient-centric", "Every design choice starts from the patient's needs, dignity and outcomes. The patient is the centre of the system — never the product."],
];

const BENEFITS = [
  ["Faster Time to Market", "Go from idea to compliant blueprint in minutes — not months of scoping."],
  ["Improved & High Quality", "Expert-grade architecture and reporting standards baked into every output."],
  ["Increased Compliance", "Outputs aligned with HIPAA, GxP, 21 CFR Part 11 and GDPR — never bolted on later."],
  ["Significantly Lower Costs", "Slash consulting and build spend with AI-accelerated delivery."],
  ["Improved Customer Experience", "Ship patient- and HCP-friendly experiences that actually delight."],
  ["Domain Expertise On Tap", "Life-science know-how from frontier models, all in one place."],
  ["De-risked Delivery", "Structured plans, audit trails and roadmaps de-risk every project."],
  ["Scales With You", "From a single prompt to enterprise workspaces with RBAC and MFA."],
  ["Ownership stays with you", "All intellectual property, data and outputs remain yours — Anxya claims no ownership over your work."],
];

const CHIPS = ["Default Compliant", "Decentralised", "Democratic", "Distributed", "Data-sovereign", "Decommodified", "Personalised", "Precision", "Patient-centric"];

export default function WhyAnxya() {
  return (
    <>
      <PageHeader
        testId="why-anxya-page"
        overline="Why Anxya"
        title={["Healthcare's data belongs", "to patients — not platforms."]}
        subtitle="Anxya is a data-sovereign, compliant-by-default health OS: you own your outputs, your data stays yours, and every workflow is built to raise care — not extract from it."
      />

      <section className="pb-20 lg:pb-24" data-testid="why-chips">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <div className="flex flex-wrap gap-3">
              {CHIPS.map((chip) => (
                <span key={chip} data-testid={`why-chip-${chip.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="rounded-full border border-primary/15 bg-white px-4 py-2 text-sm font-semibold text-primary">
                  {chip}
                </span>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white border-y border-primary/10" data-testid="agentic-era">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-14 lg:grid-cols-12 items-center">
          <div className="lg:col-span-6">
            <FadeUp>
              <Overline>The Agentic Era</Overline>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-primary">
                A once-in-a-generation chance to leave the elephants behind.
              </h2>
              <p className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
                For decades, health and life sciences ran on elephant-sized incumbents, point-solution vendors and heavyweight service providers — slow, siloed, centralised and expensive. Autonomous AI agents change the equation: you no longer have to rent capability back from giants.
              </p>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
                This is the moment to migrate to a lean, autonomous, decentralised, distributed and data-sovereign platform — and move faster, at a fraction of the cost.
              </p>
            </FadeUp>
          </div>
          <div className="lg:col-span-6">
            <FadeUp delay={0.15}>
              <div className="overflow-hidden rounded-3xl border border-primary/10">
                <img src="/assets/elephant-ai.png" alt="A legacy elephant dissolving into a distributed network of autonomous agents" className="w-full object-cover" loading="lazy" />
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="why-comparison">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-6 lg:grid-cols-2">
          <FadeUp>
            <div className="h-full rounded-3xl border border-primary/10 bg-white p-8 lg:p-10">
              <Overline>What you're leaving behind</Overline>
              <div className="mt-8 space-y-5">
                {LEAVING.map((item) => (
                  <div key={item} className="flex gap-3">
                    <XCircle size={22} weight="fill" className="mt-0.5 shrink-0 text-red-500" />
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>
          <FadeUp delay={0.12}>
            <div className="h-full rounded-3xl border border-accent/30 bg-accent/5 p-8 lg:p-10">
              <Overline>What you're moving to</Overline>
              <div className="mt-8 space-y-5">
                {MOVING_TO.map((item) => (
                  <div key={item} className="flex gap-3">
                    <CheckCircle size={22} weight="fill" className="mt-0.5 shrink-0 text-accent" />
                    <p className="text-sm sm:text-base text-primary/80 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-primary text-white" data-testid="why-structure">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Overline>The difference structure makes</Overline>
            <h2 className="mt-4 max-w-3xl font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
              The same agents — pointed at a different purpose.
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            <FadeUp delay={0.08}>
              <div className="h-full rounded-3xl border border-white/15 bg-white/5 p-8 lg:p-10">
                <h3 className="font-display text-2xl font-medium tracking-tight">Large, centralised incumbents</h3>
                <p className="mt-5 text-sm sm:text-base text-white/65 leading-relaxed">
                  Incumbents can bolt agentic AI onto their platforms, but their centralised structure keeps data, value and control flowing back to them. Agents on a centralised core automate the same lock-in: faster extraction, not real benefit to you.
                </p>
              </div>
            </FadeUp>
            <FadeUp delay={0.16}>
              <div className="h-full rounded-3xl border border-accent/40 bg-accent/10 p-8 lg:p-10">
                <h3 className="font-display text-2xl font-medium tracking-tight">Anxya Health</h3>
                <p className="mt-5 text-sm sm:text-base text-white/75 leading-relaxed">
                  Decentralised, data-sovereign and distributed by default. Agents operate where your data lives, under your control, so the benefits are real: speed, savings, sovereignty and compliance you can actually see.
                </p>
              </div>
            </FadeUp>
          </div>
          <FadeUp delay={0.22}>
            <p className="mt-10 max-w-4xl text-base sm:text-lg text-white/70 leading-relaxed">
              Frontier models are available to every platform. What changes is what they are aligned to. On Anxya, intelligence works within decentralisation, democratisation, distribution and data sovereignty — same intelligence, a different master, and the outcomes follow.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white border-b border-primary/10" data-testid="six-ds">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Overline>The 6 Ds of Anxya</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-primary">
              The core ethos behind everything we build
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {SIX_DS.map(([title, text], i) => (
              <FadeUp key={title} delay={i * 0.04}>
                <div className="h-full rounded-2xl border border-primary/10 bg-background p-7" data-testid={`six-d-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <h3 className="font-display text-xl font-medium tracking-tight text-primary">{title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="three-ps">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Overline>The 3 Ps</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-primary">
              Personalised. Precision. Patient-centric.
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-4 md:grid-cols-3">
            {THREE_PS.map(([title, text], i) => (
              <FadeUp key={title} delay={i * 0.07}>
                <div className="h-full rounded-2xl border border-primary/10 bg-white p-8" data-testid={`three-p-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <h3 className="font-display text-2xl font-medium tracking-tight text-primary">{title}</h3>
                  <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <div className="mt-10 grid gap-4 md:grid-cols-3">
              {["You own everything", "Compliance-aligned", "No investor pressure"].map((item) => (
                <div key={item} className="rounded-2xl border border-accent/30 bg-accent/5 p-6" data-testid={`why-proof-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <p className="text-sm font-bold text-primary">{item}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white border-y border-primary/10" data-testid="why-benefits">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Overline>Compliant software & strategy, at the speed of a prompt</Overline>
            <h2 className="mt-4 max-w-3xl font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-primary">
              Months of specialist work, compressed into minutes.
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map(([title, text], i) => (
              <FadeUp key={title} delay={i * 0.035}>
                <div className="h-full rounded-2xl border border-primary/10 bg-background p-7">
                  <CheckCircle size={22} weight="fill" className="text-accent" />
                  <h3 className="mt-5 font-display text-xl font-medium tracking-tight text-primary">{title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="why-cta">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <div className="rounded-3xl bg-primary p-10 lg:p-16 text-white flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
              <div className="max-w-2xl">
                <Overline>Make the move</Overline>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight">
                  Lean, autonomous, decentralised and data-sovereign — built for personalised, precision, patient-centric care.
                </h2>
              </div>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/demo"
                  data-testid="why-demo-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-accent px-7 py-3.5 text-sm font-semibold text-primary transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/30"
                >
                  Talk to Us <ArrowUpRight size={16} weight="bold" />
                </Link>
                <Link
                  to="/story"
                  data-testid="why-story-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-accent"
                >
                  Read Our Story
                </Link>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
